import { useEffect, useRef, useState, useCallback } from 'react';

type AnimationVariant =
  | 'fade-up'
  | 'fade-down'
  | 'fade-left'
  | 'fade-right'
  | 'zoom-in'
  | 'zoom-out'
  | 'flip-up'
  | 'blur-in';

interface ScrollAnimationOptions {
  threshold?: number;
  rootMargin?: string;
  triggerOnce?: boolean;
  delay?: number;
}

/**
 * Hook that observes an element and reports its visibility within the viewport.
 */
export function useScrollAnimation(options: ScrollAnimationOptions = {}) {
  const {
    threshold = 0.15,
    rootMargin = '50px 0px',
    triggerOnce = true,
  } = options;

  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) observer.unobserve(element);
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold, rootMargin, triggerOnce]);

  return { ref, isVisible };
}

/**
 * Mapping of animation variants to Tailwind‑compatible utility classes.
 */
const variantStyles: Record<
  AnimationVariant,
  { hidden: string; visible: string }
> = {
  'fade-up': {
    hidden: 'opacity-0 translate-y-8',
    visible: 'opacity-100 translate-y-0',
  },
  'fade-down': {
    hidden: 'opacity-0 -translate-y-8',
    visible: 'opacity-100 translate-y-0',
  },
  'fade-left': {
    hidden: 'opacity-0 -translate-x-8',
    visible: 'opacity-100 translate-x-0',
  },
  'fade-right': {
    hidden: 'opacity-0 translate-x-8',
    visible: 'opacity-100 translate-x-0',
  },
  'zoom-in': {
    hidden: 'opacity-0 scale-95',
    visible: 'opacity-100 scale-100',
  },
  'zoom-out': {
    hidden: 'opacity-0 scale-105',
    visible: 'opacity-100 scale-100',
  },
  'flip-up': {
    hidden: 'opacity-0 [transform:perspective(800px)_rotateX(8deg)]',
    visible: 'opacity-100 [transform:perspective(800px)_rotateX(0deg)]',
  },
  'blur-in': {
    hidden: 'opacity-0 blur-[2px]',
    visible: 'opacity-100 blur-0',
  },
};

/**
 * Generates a full class string for a given visibility state and animation variant.
 */
export function getAnimationClass(
  isVisible: boolean,
  variant: AnimationVariant = 'fade-up',
  duration: number = 700,
  delay: number = 0
): string {
  const styles = variantStyles[variant];
  const base =
    'transition-[opacity,transform,filter] ease-out will-change-[opacity,transform]';
  const durationClass = `duration-[${duration}ms]`;
  const delayClass = delay > 0 ? `delay-[${delay}ms]` : '';
  const state = isVisible ? styles.visible : styles.hidden;

  return `${base} ${durationClass} ${delayClass} ${state}`.trim();
}

/**
 * Hook that returns an array of delay values for staggered child animations.
 */
export function useStaggerChildren(
  isVisible: boolean,
  count: number,
  baseDelay: number = 0,
  staggerMs: number = 100
): number[] {
  // When the parent isn't visible we still return the delay array
  // so that consumers can map it safely without extra checks.
  return Array.from({ length: count }, (_, i) => baseDelay + i * staggerMs);
}

/**
 * Hook that creates a simple vertical parallax effect.
 * ✅ OPTIMIZED: Batches all DOM reads in a single rAF to prevent forced reflows.
 */
export function useParallax(speed: number = 0.3) {
  const ref = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);
  const rafId = useRef<number>(0);
  const ticking = useRef(false);

  const handleScroll = useCallback(() => {
    // Debounce: only schedule one rAF at a time
    if (ticking.current) return;
    ticking.current = true;

    rafId.current = requestAnimationFrame(() => {
      if (!ref.current) {
        ticking.current = false;
        return;
      }

      // ✅ BATCH READ PHASE: Read all geometric properties at once
      const rect = ref.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      // Calculate offset from batched reads
      const elementCenter = rect.top + rect.height / 2;
      const distanceFromCenter = elementCenter - windowHeight / 2;
      const newOffset = distanceFromCenter * speed * -1;

      // ✅ WRITE PHASE: Update state (triggers re-render, no DOM writes here)
      setOffset(newOffset);
      
      ticking.current = false;
    });
  }, [speed]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    // Initialize on mount
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [handleScroll]);

  return { ref, offset };
}

/**
 * Hook that animates a numeric count‑up when the element becomes visible.
 */
export function useCountUp(
  target: number,
  isVisible: boolean,
  duration: number = 2000
): number {
  const [count, setCount] = useState(0);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!isVisible || hasAnimated.current) return;
    hasAnimated.current = true;

    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease‑out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * target));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, target, duration]);

  return count;
}